// O ÚNICO lugar das edge functions que conhece idioma.
//
// Havia sete cópias do normalizador (quatro `normalizeLang`, duas `langKeyOf`,
// uma embutida no localizeContents) mais NOVE cópias idênticas de
// `normalizeLocale` na família *-extract. Com três idiomas isso já era
// manutenção duplicada; com seis viram 54 pontos que precisam concordar entre
// si — e o histórico do repo diz que não concordam: foi exatamente assim que o
// mirror-chat passou meses servindo cabeçalho em português para todo usuário
// EN e NL.
//
// Regra daqui em diante: idioma entra por aqui e sai no formato que o chamador
// pediu. Idioma novo é uma linha em LANGS.

/** Chave curta — a que os mapas de copy usam. ISO-639-1. */
export type LangKey = "pt" | "en" | "nl" | "es" | "fr" | "it" | "de" | "pl" | "bg";

/**
 * Código de locale das functions. NÃO é o BCP-47: a família *-extract e o
 * _shared/datetime-natural historicamente usam "pt-BR" para português e o
 * código curto para o resto.
 */
export type EdgeLocale = "pt-BR" | "en" | "nl" | "es" | "fr" | "it" | "de" | "pl" | "bg";

interface LangDef {
  key: LangKey;
  /** Locale das functions. */
  locale: EdgeLocale;
  /** BCP-47, para Intl. */
  tag: string;
  /** Como o idioma é nomeado DENTRO do prompt, em inglês. */
  label: string;
  /** Como o modelo deve ser instruído a responder, no próprio idioma. */
  respondIn: string;
}

/** Espelha src/i18n/locale.ts LANGUAGES. Um idioma novo é uma linha aqui. */
export const LANGS: readonly LangDef[] = [
  { key: "pt", locale: "pt-BR", tag: "pt-BR", label: "Brazilian Portuguese", respondIn: "português brasileiro" },
  { key: "en", locale: "en", tag: "en-US", label: "English", respondIn: "English" },
  { key: "nl", locale: "nl", tag: "nl-NL", label: "Dutch", respondIn: "Nederlands" },
  { key: "es", locale: "es", tag: "es-ES", label: "Spanish (Spain)", respondIn: "español de España" },
  { key: "fr", locale: "fr", tag: "fr-FR", label: "French (France)", respondIn: "français" },
  { key: "it", locale: "it", tag: "it-IT", label: "Italian (Italy)", respondIn: "italiano" },
  { key: "de", locale: "de", tag: "de-DE", label: "German (Germany)", respondIn: "Deutsch" },
  { key: "pl", locale: "pl", tag: "pl-PL", label: "Polish", respondIn: "polski" },
  { key: "bg", locale: "bg", tag: "bg-BG", label: "Bulgarian", respondIn: "български" },
];

/** Maintained UI review languages; the frozen Bulgarian dictionary uses existing fallbacks. */
export type ActiveLangKey = Exclude<LangKey, "bg">;
export const ACTIVE_LANGS = LANGS.filter(
  (def): def is LangDef & { key: ActiveLangKey } => def.key !== "bg",
);
export const BASE_LANG_KEY: LangKey = "en";
export function activeLanguageKeyOf(key: LangKey, fallback: ActiveLangKey = "en"): ActiveLangKey {
  return ACTIVE_LANGS.find(def => def.key === key)?.key ?? fallback;
}

/**
 * O código de locale do PORTAL e do dicionário — o terceiro vocabulário.
 *
 * Já existiam dois: `key` (ISO-639-1, o que os mapas de copy usam) e `locale`
 * (o que as functions falam, com "pt-BR" para o português). Este é o que o
 * arquivo de dicionário, o glossário e a coluna
 * `organization_announcement_deliveries.locale` usam — "es-ES", não "es".
 * Misturar os dois faz a view vw_partner_announcements agrupar "es" e "es-ES"
 * como idiomas diferentes na mesma tela.
 *
 * Estava escrito à mão em QUATRO lugares (announcementTranslate, partnerBrief,
 * partner-agents, partner-invite), todos parados em seis idiomas depois do
 * lançamento dos oito. Agora é descrito uma vez.
 *
 * `satisfies Record<LangKey, string>` é a parte que trabalha: obriga os nove a
 * estarem aqui, E preserva o tipo literal de cada valor — sem ele,
 * `PortalLocale` viraria `string` e todo `Record<PortalLocale, …>` deixaria de
 * exigir idioma nenhum.
 */
export const PORTAL_LOCALE = {
  pt: "pt-BR",
  en: "en",
  nl: "nl",
  es: "es-ES",
  fr: "fr",
  it: "it",
  de: "de",
  pl: "pl",
  bg: "bg",
} as const satisfies Record<LangKey, string>;

/** Um dos códigos de locale do portal. União literal, não `string`. */
export type PortalLocale = (typeof PORTAL_LOCALE)[LangKey];
export type ActivePortalLocale = Exclude<PortalLocale, "bg">;
export const APP_LANGUAGE_CODES: readonly Uppercase<EdgeLocale>[] = LANGS.map(def => def.locale.toUpperCase() as Uppercase<EdgeLocale>);

/** Os locales do portal, na ordem de exibição do produto. */
export const PORTAL_LOCALES: readonly PortalLocale[] = LANGS.map((d) => PORTAL_LOCALE[d.key]);

/** Mercado de lançamento. Destino de todo desconhecido — de forma AUDÍVEL. */
export const DEFAULT_LANG_KEY: LangKey = "pt";

const BY_KEY = new Map<LangKey, LangDef>(LANGS.map((d) => [d.key, d]));

/**
 * Reconhece um código de idioma sem decidir o que fazer quando não reconhece.
 *
 * Devolver `null` em vez de "pt" é o ponto: separa "o usuário fala português"
 * de "não sei que idioma é este". Os chamadores que precisam de um valor usam
 * `langKeyOf`, que registra a coerção; quem precisa saber a diferença usa este.
 */
export function tryLangKey(language: string | null | undefined): LangKey | null {
  const raw = (language || "").trim().toLowerCase();
  if (!raw) return null;
  // Prefixo: "PT-BR", "pt_BR", "en-US", "es-419" e "bg" todos resolvem.
  const prefix = raw.replace("_", "-").split("-")[0];
  for (const def of LANGS) if (def.key === prefix) return def.key;
  return null;
}

/**
 * Código de idioma → chave curta, com o desconhecido caindo no mercado de
 * lançamento — e DEIXANDO RASTRO.
 *
 * O console.error é o ponto da mudança, não um detalhe. Antes, um `bg` que
 * chegasse aqui virava `pt` em silêncio, indistinguível de um `null`: o app
 * ficava em búlgaro, o Espelho respondia em português, e não havia UM evento
 * em lugar nenhum ligando as duas coisas. Pela §11 do CLAUDE.md o
 * console.error vira evento no Sentry, então a coerção passa a ser achável.
 *
 * `null`/vazio NÃO registra: "usuário sem idioma definido" é estado normal
 * (pré-onboarding), não anomalia. Só o valor PRESENTE e não reconhecido grita.
 */
export function langKeyOf(language: string | null | undefined, context?: string): LangKey {
  const hit = tryLangKey(language);
  if (hit) return hit;
  if (language && String(language).trim()) {
    console.error(
      `[lang] código de idioma não reconhecido: ${JSON.stringify(language)} → caindo em ` +
        `"${DEFAULT_LANG_KEY}"${context ? ` (${context})` : ""}`,
    );
  }
  return DEFAULT_LANG_KEY;
}

/** Locale das functions ("pt-BR" | "en" | ...), com o mesmo rastro. */
export function edgeLocaleOf(language: string | null | undefined, context?: string): EdgeLocale {
  return BY_KEY.get(langKeyOf(language, context))!.locale;
}

/** BCP-47 para Intl. */
export function localeTagOf(language: string | null | undefined, context?: string): string {
  return BY_KEY.get(langKeyOf(language, context))!.tag;
}

/** Nome do idioma em inglês, para escrever dentro de um prompt. */
export function languageLabelOf(language: string | null | undefined, context?: string): string {
  return BY_KEY.get(langKeyOf(language, context))!.label;
}

/**
 * A instrução de idioma que vai no system prompt, escrita NO idioma alvo.
 *
 * Escrever "responda em búlgaro" em inglês funciona pior do que escrever a
 * instrução no próprio idioma: o texto no idioma alvo é o sinal mais forte que
 * o prompt pode dar. Por isso `respondIn` é o endônimo, não o exônimo.
 */
export function respondInLanguage(language: string | null | undefined, context?: string): string {
  const def = BY_KEY.get(langKeyOf(language, context))!;
  return `Responda SEMPRE em ${def.respondIn} (${def.label}), qualquer que seja o idioma do contexto.`;
}

/** Todos os locales aceitos — para validar entrada sem repetir a lista. */
export function isSupportedLocale(value: unknown): boolean {
  return typeof value === "string" && tryLangKey(value) !== null;
}
